import { pick } from 'lodash';
import moment, { Moment } from 'moment';
import { LocationQueryRaw, RouteLocationNamedRaw, RouteLocationNormalizedLoaded, RouteRecordName } from 'vue-router';
import { DossierId } from '@/dossier/models/AbstractDossier';
import { WorkshopTeamId } from '@/general/models/WorkshopTeam';
import { EMPTY_PARAM_VALUE, buildRouteParams } from '@/general/router/util';
import { parseNumericId } from '@/shared/converters/commonConverters';
import Outlet, { OutletId } from '@/shared/models/Outlet';
import { enumValues, isPresent, Nullish, present } from '@/shared/utils/typescript';
import PlannerDossierCategory from '../constants/PlannerDossierCategory';
import PlannerView from '../constants/PlannerView';
import { PLANNER_ROOT_ROUTE, ROUTE_DATE_FORMAT } from '../constants/general';
import { PlannerEventId } from '../models/ids';

export interface PlannerRouteParams {
  date?: string;
  dossierId?: string;
  eventId?: string;
  view?: string;
  outlet?: string;
  team?: string;
  mine?: string;
  waiting?: string;
  user?: string;
  confirm?: string;
  dossierCategory?: string;
  refDate?: string;
  start?: string;
  end?: string;
  resource?: string;
}

export interface ValidateTeamId {
  (outletId: OutletId, teamId: WorkshopTeamId): boolean;
}

export function sanitizeDossierCategory(value: Nullish<string>): PlannerDossierCategory | null {
  if (enumValues<any>(PlannerDossierCategory).includes(value)) {
    return value as PlannerDossierCategory;
  } else {
    return null;
  }
}

export default class PlannerParams {
  constructor(
    public date: Moment,
    public view: PlannerView,
    public outletId: OutletId,
    public teamId: WorkshopTeamId | null,
    public showMyDossiers: boolean,
    public showWaiting: boolean,
    public showUser: boolean,
    public dossierId: DossierId | null,
    public eventId: PlannerEventId | null,
    public showConfirmation: boolean,
    public dossierCategory: PlannerDossierCategory | null,
    public referenceDate: Moment | null,
    public corrected: boolean,
  ) {
  }

  public static basic(date: Moment, outletId: OutletId, teamId: WorkshopTeamId | null = null): PlannerParams {
    return new PlannerParams(date, PlannerView.DAY, outletId, teamId, false, false, false, null, null, false, null, null, false);
  }

  public static parse(raw: PlannerRouteParams, current: PlannerParams, outlets: Array<Outlet>, validateTeamId: ValidateTeamId): PlannerParams {
    let corrected = false;

    let date: Moment;
    if (raw.date) {
      date = moment(raw.date, ROUTE_DATE_FORMAT);
      if (!date.isValid()) {
        date = current.date;
        corrected = true;
      }
    } else {
      date = moment();
      corrected = true;
    }

    let view: PlannerView = Number(raw.view);
    if (!enumValues(PlannerView).includes(view)) {
      view = current.view;
      corrected = true;
    }

    let outletId = parseNumericId<OutletId>(raw.outlet);
    if (!outlets.some(o => o.id === outletId)) {
      // BUG FIX: MOBOWEB-2918 - When switching accounts the Outlet[] might not contain the current.outletId. Fallback to the first Outlet.id
      outletId = outlets.find(o => o.id === current.outletId)?.id ?? outlets[0]?.id;
      corrected = true;
    }

    let teamId = parseNumericId<WorkshopTeamId>(raw.team);
    let finalTeamId = teamId ?? current.teamId;
    if (raw.team === EMPTY_PARAM_VALUE || outletId === null) finalTeamId = null;
    if (finalTeamId !== null && !validateTeamId(present(outletId), finalTeamId)) finalTeamId = null;
    if (teamId !== finalTeamId) {
      teamId = finalTeamId;
      corrected = true;
    }

    const myDossiers = raw.mine === 'y';
    const waiting = raw.waiting === 'y';
    const user = raw.user === 'y';

    const dossierId = parseNumericId<DossierId>(raw.dossierId);
    const eventId = parseNumericId<PlannerEventId>(raw.eventId);
    const confirmation = raw.confirm === 'y';

    let dossierCategory = sanitizeDossierCategory(raw.dossierCategory);
    if (raw.dossierCategory && !dossierCategory) {
      dossierCategory = current.dossierCategory;
      corrected = true;
    }

    let referenceDate: Moment | null = null;
    if (raw.refDate) {
      referenceDate = moment(raw.refDate, ROUTE_DATE_FORMAT);
      if (!referenceDate.isValid()) {
        referenceDate = null;
        corrected = true;
      }
    }

    return new PlannerParams(date, view, present(outletId), teamId, myDossiers, waiting, user, dossierId, eventId, confirmation, dossierCategory, referenceDate, corrected);
  }

  withData(data: Partial<PlannerParams>): PlannerParams {
    return new PlannerParams(
      data.date === undefined ? this.date : data.date,
      data.view === undefined ? this.view : data.view,
      data.outletId === undefined ? this.outletId : data.outletId,
      data.teamId === undefined ? this.teamId : data.teamId,
      data.showMyDossiers === undefined ? this.showMyDossiers : data.showMyDossiers,
      data.showWaiting === undefined ? this.showWaiting : data.showWaiting,
      data.showUser === undefined ? this.showUser : data.showUser,
      data.dossierId === undefined ? this.dossierId : data.dossierId,
      data.eventId === undefined ? this.eventId : data.eventId,
      data.showConfirmation === undefined ? this.showConfirmation : data.showConfirmation,
      data.dossierCategory === undefined ? this.dossierCategory : data.dossierCategory,
      data.referenceDate === undefined ? this.referenceDate : data.referenceDate,
      false,
    );
  }

  withDate(date: Moment): PlannerParams {
    return this.withData({ date });
  }

  withView(view: PlannerView): PlannerParams {
    return this.withData({ view });
  }

  withOutlet(outletId: OutletId): PlannerParams {
    return this.withData({ outletId });
  }

  withTeam(teamId: WorkshopTeamId | null): PlannerParams {
    return this.withData({ teamId });
  }

  withShowMyDossiers(enabled: boolean): PlannerParams {
    return this.withData({ showMyDossiers: enabled });
  }

  withShowWaiting(enabled: boolean): PlannerParams {
    return this.withData({ showWaiting: enabled });
  }

  withShowUser(enabled: boolean): PlannerParams {
    return this.withData({ showUser: enabled });
  }

  withDossierId(dossierId: DossierId): PlannerParams {
    return this.withData({ dossierId });
  }

  withEventId(eventId: PlannerEventId): PlannerParams {
    return this.withData({ eventId });
  }

  withConfirmation(enabled: boolean): PlannerParams {
    return this.withData({ showConfirmation: enabled });
  }

  withDossierCategory(dossierCategory: PlannerDossierCategory | null): PlannerParams {
    return this.withData({ dossierCategory });
  }

  withReferenceDate(referenceDate: Moment | null): PlannerParams {
    return this.withData({ referenceDate });
  }

  primary(): PlannerParams {
    return this.withData(pick(this, ['view', 'outletId', 'teamId', 'showMyDossiers', 'showWaiting', 'showUser']));
  }

  asLocation(name: RouteRecordName, query: LocationQueryRaw = {}): RouteLocationNamedRaw {
    return {
      name,
      params: buildRouteParams({
        date: this.date,
        /*
          When a dossierId and/or eventId is present, it will be included in every route generated (for button links for example),
          even though that link might not expect it, which throws a warning. That's why we check the route name before adding the params.
         */
        dossierId: name === 'planner-dossier' && isPresent(this.dossierId) ? this.dossierId : undefined,
        eventId: name === 'planner-event' && isPresent(this.eventId) ? this.eventId : undefined,
      }),
      query: buildRouteParams({
        view: this.view,
        outlet: this.outletId,
        team: this.teamId,
        mine: this.showMyDossiers,
        waiting: this.showWaiting,
        user: this.showUser,
        confirm: this.showConfirmation,
        dossierCategory: this.dossierCategory ?? undefined,
        refDate: this.referenceDate ?? undefined,
        dossierId: this.dossierId ?? undefined,
        ...query,
      }),
    };
  }

  asCurrentLocation(route: RouteLocationNormalizedLoaded, query: LocationQueryRaw = {}): RouteLocationNamedRaw {
    const name = (route.matched.some(r => r.meta?.inPlanner) && route.name) || PLANNER_ROOT_ROUTE;
    return this.asLocation(name, query);
  }

  asRootLocation(query: LocationQueryRaw = {}): RouteLocationNamedRaw {
    return this.asLocation(PLANNER_ROOT_ROUTE, query);
  }

  asEventLocation(eventId: PlannerEventId, confirmation = false, dossierId: DossierId | null = null): RouteLocationNamedRaw {
    const query = dossierId ? { dossierId: String(dossierId) } : {};
    return this.withData({
      eventId,
      showConfirmation: confirmation,
    }).asLocation('planner-event', query);
  }

  asDossierLocation(dossierId: DossierId): RouteLocationNamedRaw {
    return this.withData({
      dossierId,
    }).asLocation('planner-dossier');
  }

  asDossierListLocation(dossierCategory: PlannerDossierCategory): RouteLocationNamedRaw {
    return this.withData({
      dossierCategory,
      dossierId: null,
    }).asLocation('planner-dossier-list');
  }
}
